<template>
    <page>
        <div class="d-flex pt-2 pb-1 paygroup-run-header">
            <div class="mr-4">
                <h5 class="text-muted">PAY GROUP</h5>
                <h4>{{ payrollRun.paygroupName }}</h4>
            </div>
            <div class="mr-4">
                <h5 class="text-muted">CHECK DATE</h5>
                <h4>{{ $filters.date(payrollRun.checkDate) }}</h4>
            </div>
            <div class="mr-4">
                <h5 class="text-muted">PAY PERIOD</h5>
                <h4>{{ $filters.date(payrollRun.payPeriodStartDate) }} - {{ $filters.date(payrollRun.payPeriodEndDate) }}</h4>
            </div>
            <payroll-steps class="ml-auto align-self-center" :current="paygridMode" :show-salaried="hasSalariedEmployees" />
        </div>
        <alert-box v-if="runStatusMessage" type="info" class="mt-2 mb-0">{{ runStatusMessage }}</alert-box>
        <div class="pt-2" v-if="payrollRun.lastSavedTime">Last Saved {{ $filters.datetimetodayaware(payrollRun.lastSavedTime) }}</div>
        <pay-grid-table
            v-if="dataLoaded && payrollRun.companies"
            :key="paygridMode"
            :client="client"
            :payroll-run="payrollRun"
            :mode="paygridMode"
            :bottom-offset="footerHeight"
            :company-earning-types="clientEarningTypes"
            @blur="onBlur"
            @open-earnings-statement="openEarningsStatement(...$event)"
            @open-earnings="openEarnings(...$event)"
            @open-modify-earnings="openModifyEarnings(...$event)"
            @add-separate-paycheck="addSeparatePaycheck(...$event)"
            @section-reopen="openReopenModal"
            @refresh="getData(true)"
        />
        <earnings-statement-preview-modal
            v-if="earningsStatementOpen && currentEmployeeId && currentEarningsStatementId"
            :client="client"
            :employee-id="currentEmployeeId"
            :earnings-statement-id="currentEarningsStatementId"
            :clientEarningTypes="clientEarningTypes"
            @close="earningsStatementOpen = false; currentEmployeeId = null; currentEarningsStatementId = null;"
            @updated="onUpdate"
        />
        <earnings-statement-edit-modal
            v-if="modifyEarningsOpen && currentModifyStatementId && currentEmployeeId"
            :client="client"
            :employee-id="currentEmployeeId"
            :earnings-statement-id="currentModifyStatementId"
            :clientEarningTypes="clientEarningTypes"
            :flow="currentModifyFlow"
            @save="onModifyEarningsSaved"
            @close="modifyEarningsOpen = false; currentEmployeeId = null; currentModifyStatementId = null;"
        />
        <earnings-statement-edit-modal
            v-if="currentSeparatePaycheckEmployeeId"
            :client="client"
            :employee-id="currentSeparatePaycheckEmployeeId"
            :create-separate-check="true"
            :run-id="payrollRun.id"
            :clientEarningTypes="clientEarningTypes"
            @save="onSeparateCheckSaved"
            @close="currentSeparatePaycheckEmployeeId = null; currentSeparatePaycheckId = null; currentSeparatePaycheckIsBonus = false;"
        />
        <modal ref="reopenModal" @close="reopenModalLocation = null" :size="'md'">
            <template v-slot:title>Re-Open {{ reopenModalLocation && reopenModalLocation.name }}</template>
            <p>This changes its status from <strong>Ready for Approval</strong> to <strong>In Progress</strong>.</p>
            <p>
                To make the location <strong>Ready for Approval</strong> again, you would have to re-submit this location.
            </p>
            <template v-slot:footer>
                <button-row inline>
                    <button type="button" class="btn btn-outline-primary" @click.prevent="$refs.reopenModal.close()">Cancel</button>
                    <button type="button" class="btn btn-primary" @click.prevent="confirmReopen">Re-Open</button>
                </button-row>
            </template>
        </modal>

        <div class="paygrid-footer" ref="footer">
            <button-row stacked>
                <button type="button" class="btn btn-outline-primary" @click.prevent="goBack">Back</button>
                <button type="button" class="btn btn-primary" @click.prevent="goToNextScreen">Continue</button>
            </button-row>
        </div>
    </page>
</template>

<script>
import PayGridTable from './components/PayGridTable'
import EarningsStatementPreviewModal from './components/EarningsStatementPreviewModal'
import EarningsStatementEditModal from './components/EarningsStatementEditModal'
import PayrollSteps from './components/PayrollSteps'
import Page from '@/components/Page'
import ButtonRow from '@/components/ButtonRow'
import Modal from '@/components/Modal'
import moment from 'moment'

export default {
    components: {PayGridTable, EarningsStatementPreviewModal, EarningsStatementEditModal, PayrollSteps, Page, ButtonRow, Modal},
    props: ['client'],
    computed: {
        // One component serves the hourly and salaried steps (route meta `paygridMode`);
        // the mode picks which earnings show and where Back/Continue go.
        paygridMode() {
            return this.$route.meta.paygridMode === 'salaried' ? 'salaried' : 'hourly'
        },
        // Until the run loads, assume the salaried step exists so the steps indicator
        // doesn't flicker between two and three steps
        hasSalariedEmployees() {
            return this.dataLoaded ? this.payrollRun.hasSalariedEmployees : true
        },
        runStatusMessage() {
            if (this.payrollRun.status === 'ready-for-approval') {
                return 'This payroll has been submitted for approval.'
            }
            if (this.payrollRun.status === 'approved') {
                return 'This payroll has been approved.'
            }
            if (this.payrollRun.status === 'completed') {
                return 'This payroll has been completed.'
            }
            return ''
        },
    },
    data() {
        return {
            dataLoaded: false,
            payrollRun: {
                id: this.$route.params.runId,
                paygroupName: '',
                // alerts: [],
                checkDate: null,
                payPeriodStartDate: null,
                payPeriodEndDate: null,
                lastSavedTime: '',
                status: '',
                companies: [],
                hasSalariedEmployees: true,
            },
            earningsStatementOpen: false,
            modifyEarningsOpen: false,
            currentEmployeeId: null,
            currentEarningsStatementId: null,
            currentModifyStatementId: null,
            currentModifyFlow: 'earnings',
            currentSeparatePaycheckEmployeeId: null,
            currentSeparatePaycheckId: null,
            currentSeparatePaycheckIsBonus: false,
            clientEarningTypes: [],
            reopenModalLocation: null,
            footerHeight: 0,
            footerObserver: null,
        }
    },
    mounted() {
        this.$store.dispatch('SET_PAGE_TITLE', 'Payroll')
        this.getData()
        this.getClientEarningTypes()
        this.watchFooterHeight()
        window.addEventListener('resize', this.measureFooter)
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.measureFooter)
        if (this.footerObserver) {
            this.footerObserver.disconnect()
            this.footerObserver = null
        }
    },
    watch: {
        // Re-measure once the run loads and the grid exists.
        dataLoaded() {
            this.measureFooter()
        },
    },
    methods: {
        watchFooterHeight() {
            // Measure the footer height (it changes when the buttons stack on small
            // screens) so the grid can leave room for it.
            this.footerObserver = new ResizeObserver(() => {
                this.measureFooter()
            })
            this.footerObserver.observe(this.$refs.footer)
        },
        measureFooter() {
            const footer = this.$refs.footer
            if (!footer) {
                return
            }
            this.footerHeight = footer.offsetHeight
            // Also reserve the padding/margins between grid and footer, measured off the
            // document so scrolling doesn't change the result.
            this.$nextTick(() => {
                window.requestAnimationFrame(() => {
                    const doc = document.documentElement
                    const belowTheFold = doc.scrollHeight - doc.clientHeight
                    if (belowTheFold > 0) {
                        this.footerHeight = footer.offsetHeight + Math.ceil(belowTheFold)
                    }
                })
            })
        },
        getData(silent) {
            if (!silent) {
                this.dataLoaded = false
                this.$store.dispatch('START_LOADING')
            }
            this.refreshing = true

            let url
            const paygroupId = this.$route.params.paygroupId
            const runId = this.$route.params.runId
            const collectionId = this.$route.params.collectionId
            const payPeriodStart = this.$route.query.pay_period_start_date
            if (collectionId) {
                url = `/clients/${this.client.id}/payroll/paygrid/collections/${collectionId}/runs?pay_period_start_date=${payPeriodStart}`
            } else if (paygroupId && runId) {
                url = `/clients/${this.client.id}/payroll/paygrid/paygroups/${paygroupId}/runs/${runId}`
            }

            // let resp = paygridData

            this.$api.get(url).then(resp => {
                this.payrollRun.companies = resp['companies']
                // this.payrollRun.lastSavedTime = moment(resp['last_saved_time'], 'LTS')
                this.payrollRun.paygroupName = resp['paygroup_name']
                this.payrollRun.payPeriodStartDate = resp['pay_period_start_date']
                this.payrollRun.payPeriodEndDate = resp['pay_period_end_date']
                this.payrollRun.checkDate = resp['check_date']
                this.payrollRun.lastSavedTime = resp['last_saved_time']
                this.payrollRun.status = resp['status'] || ''
                this.payrollRun.location_statuses = resp['location_statuses']
                this.payrollRun.hasSalariedEmployees = Boolean(resp['has_salaried_employees'])

                this.dataLoaded = true
                this.refreshing = false
                if (!silent) {
                    this.$store.dispatch('STOP_LOADING')
                }

                // The salaried step is skipped for this run; send a direct visit to the hourly step
                if (this.paygridMode === 'salaried' && !this.payrollRun.hasSalariedEmployees) {
                    this.$router.replace({path: this.hourlyPath()})
                }
            }).catch((errors) => {
                if (errors.__status__ == 403) {
                    this.$store.dispatch('STOP_LOADING')
                    this.$bus.$emit('no-access')
                    return
                }
                if (!silent) {
                    this.$store.dispatch('STOP_LOADING')
                }
                this.refreshing = false
                this.$bus.showError(errors.__all__)
            })
        },
        getClientEarningTypes() {
            return this.$api.get( `/clients/${this.client.id}/earning-types`).then(resp => {
                this.clientEarningTypes = resp
            })
        },
        onBlur(msg) {
            // cell_meta carries direct refs to the live model objects (see PayGridTable),
            // so we mutate in place and save — no key reconstruction or lookup.
            const earning = msg.cell_meta.earning
            const paycheck = msg.cell_meta.paycheck
            const field = msg.cell_meta.field
            earning[field] = msg.value
            if (field === 'amount') {
                earning.is_amount_entered = msg.value !== ''
            }
            this.saveStatementEarnings(paycheck.earnings_statement_id, paycheck.id, paycheck.earnings)
        },
        onUpdate(paycheck) {
            console.log('onUpdate')
            console.log(paycheck)
            const paycheckObj = { // we only need a subset for the table
                id: paycheck.id,
                earnings: paycheck.earnings,
                is_separate_check: paycheck.is_separate_check
            }
            this.setPaycheck(paycheck.company_id, paycheck.work_location_id, paycheck.employee_id, paycheck.id, paycheckObj)
        },
        setPaycheck(companyId, locationId, employeeId, paycheckId, paycheck) {
            let companyIdx = this.payrollRun.companies.findIndex(c => c.id === companyId)
            let company = this.payrollRun.companies[companyIdx]
            let locationIdx = company.locations.findIndex(l => l.id === locationId)
            let location = company.locations[locationIdx]
            let employeeIdx = location.employees.findIndex(emp => emp.id === employeeId)
            let employee = location.employees[employeeIdx]
            let paycheckIdx = employee.paychecks.findIndex(pc => pc.id === paycheckId)
            if (paycheckIdx == -1) {
                employee.paychecks.push(paycheck)
            } else {
                employee.paychecks[paycheckIdx] = paycheck
            }
            return paycheck
        },
        saveStatementEarnings(earningsStatementId, paycheckId, earnings) {
            // Only send the edited paycheck's earnings; the backend pulls the rest of the statement's earnings
            // from the DB and re-calculates overtime blending across all of the employee's paychecks within the statement
            const url = `/clients/${this.client.id}/payroll/paygrid/earnings-statements/${earningsStatementId}/base-paychecks`
            const earningsByPaycheckId = {}
            earningsByPaycheckId[paycheckId] = earnings
            this.$api.post(url, {action: 'save', earnings_by_paycheck_id: earningsByPaycheckId}).then(resp => {
                this.payrollRun.lastSavedTime = resp['last_saved_time']
                this.applyStatementPaychecks(resp['paychecks'])
            }).catch((errors) => {
                if (errors.__status__ == 403) {
                    this.$bus.$emit('no-access')
                    return
                }
                this.$bus.showError(errors.__all__)
            })
        },
        applyStatementPaychecks(paychecks) {
            // Sync every paycheck in the statement back into the grid; the blended overtime rate can
            // change amounts on paychecks the user didn't directly edit.
            paychecks.forEach(paycheck => {
                const paycheckObj = {
                    id: paycheck.id,
                    earnings_statement_id: paycheck.earnings_statement_id,
                    earnings: paycheck.earnings,
                    is_separate_check: paycheck.is_separate_check,
                }
                this.setPaycheck(paycheck.company_id, paycheck.work_location_id, paycheck.employee_id, paycheck.id, paycheckObj)
            })
        },
        onModifyEarningsSaved(paychecks) {
            this.applyStatementPaychecks(paychecks)
        },
        openEarningsStatement(employeeId, earningsStatementId) {
            this.earningsStatementOpen = true
            this.currentEmployeeId = employeeId
            this.currentEarningsStatementId = earningsStatementId
        },
        openModifyEarnings(employeeId, earningsStatementId, flow) {
            this.modifyEarningsOpen = true
            this.currentEmployeeId = employeeId
            this.currentModifyStatementId = earningsStatementId
            this.currentModifyFlow = flow || 'earnings'
        },
        addSeparatePaycheck(employeeId, isBonus) {
            this.currentSeparatePaycheckEmployeeId = employeeId
            this.currentSeparatePaycheckIsBonus = isBonus
        },
        onSeparateCheckSaved() {
            // A new statement + paycheck were created; refresh the grid so the check appears.
            this.currentSeparatePaycheckEmployeeId = null
            this.currentSeparatePaycheckId = null
            this.currentSeparatePaycheckIsBonus = false
            this.getData(true)
        },
        openReopenModal(location) {
            this.reopenModalLocation = location
            this.$refs.reopenModal.open()
        },
        confirmReopen() {
            const location = this.reopenModalLocation
            this.$refs.reopenModal.close()
            const url = `/clients/${this.client.id}/payroll/runs/${this.payrollRun.id}/reopen-location`
            this.$api.post(url, { location_id: location.id }).then(resp => {
                this.payrollRun.status = resp.status
                this.payrollRun.location_statuses = resp.location_statuses
            }).catch(errors => {
                this.$bus.showError(errors.__all__)
            })
        },
        hourlyPath() {
            return `/clients/${this.$route.params.hashedClientId}/payroll/paygrid/paygroup/${this.$route.params.paygroupId}/run/${this.$route.params.runId}`
        },
        goBack() {
            if (this.paygridMode === 'salaried') {
                this.$router.push({path: this.hourlyPath()})
                return
            }
            this.$router.push({
                path: `/clients/${this.$route.params.hashedClientId}/payroll/`,
            })
        },
        goToNextScreen() {
            // TODO KB PG logic for collection summary screen
            if (this.paygridMode === 'salaried' || !this.hasSalariedEmployees) {
                this.$router.push({path: `${this.hourlyPath()}/summary`})
                return
            }
            this.$router.push({path: `${this.hourlyPath()}/salaried`})
        }
    },
}
</script>

<style lang="scss" scoped>
    .paygroup-run-header {
        h5 {
            font-weight: 500;
        }
        border-bottom: 1px solid #ddd;
    }
</style>
